// ─────────────────────────────────────────────────────────────────────────────
// Mensajes de error del acceso Frontline (registro, verificación de correo, login).
//
// El backend responde el `title` SÓLO en español, y estas pantallas hablan 7 idiomas. Por eso el
// servidor adjunta un código de motivo en `errors.reason` (ver `getRecognitionErrorReason`) y aquí se
// traduce por el código. Si el código no se conoce se cae al texto del servidor, que es lo que había
// antes — nunca se inventa un mensaje.
//
// Lo que NO se hace aquí a propósito: distinguir por qué falló el reclamo contra el padrón. Todos esos
// motivos llegan con el mismo código (`datos-no-validados`): distinguirlos convertiría el formulario en
// un buscador de qué nóminas existen y cuáles ya se reclamaron.
// ─────────────────────────────────────────────────────────────────────────────
import type { TFunction } from 'i18next';
import {
  NETWORK_ERROR_STATUS,
  NO_AUTH_TOKEN_STATUS,
  getRecognitionErrorCorrelationId,
  getRecognitionErrorMessage,
  getRecognitionErrorReason,
  getRecognitionErrorStatus,
} from '@/api/recognition';

/** Dominio de un correo en minúsculas, o `null` si no hay exactamente un `@` con algo a cada lado. */
export function dominioDeCorreo(correo: string): string | null {
  const limpio = correo.trim();
  const arroba = limpio.indexOf('@');
  if (arroba <= 0 || arroba !== limpio.lastIndexOf('@') || arroba === limpio.length - 1) return null;
  return limpio.slice(arroba + 1).toLowerCase();
}

/**
 * ¿El correo es de un dominio corporativo (de los que entran por SSO)?
 *
 * Es un ESPEJO de `SsoEmailDomains` del servidor (la lista de dominios de SSO, que llega por
 * `GET v1/users/sso-domains`), y el servidor es quien manda: este endpoint es anónimo y el navegador se
 * salta con un `curl`. Aquí sólo sirve para avisar ANTES de enviar. Misma regla: dominio EXACTO (nunca por
 * sufijo) y lista vacía o ausente ⇒ nadie es corporativo.
 */
export function esCorreoCorporativo(correo: string, dominios: readonly string[] | undefined): boolean {
  if (!dominios || dominios.length === 0) return false;
  const dominio = dominioDeCorreo(correo);
  if (!dominio) return false;
  return dominios.some((d) => d.trim().toLowerCase() === dominio);
}

/** ¿Falló antes de tener una respuesta útil (sin conexión, sin sesión, servidor caído o saturado)? */
export function esErrorTemporal(error: unknown): boolean {
  const status = getRecognitionErrorStatus(error);
  if (status === undefined) return true; // un `fetch` que ni siquiera produjo un error tipado
  return status === 0
    || status === NETWORK_ERROR_STATUS
    || status === NO_AUTH_TOKEN_STATUS
    || status === 429
    || status >= 500;
}

/** Códigos de motivo del registro (`RegistrationReasons` del servidor) → clave de traducción + texto por omisión. */
const CLAVES_DE_REGISTRO: Readonly<Record<string, readonly [string, string]>> = {
  'correo-obligatorio': ['register.errEmail', 'Introduce un correo electrónico válido.'],
  'correo-invalido': ['register.errEmail', 'Introduce un correo electrónico válido.'],
  'password-invalida': ['register.errPassword', 'La contraseña debe tener entre 8 y 256 caracteres.'],
  'ou-obligatoria': ['register.errOu', 'Selecciona tu organización.'],
  'ou-no-encontrada': ['register.errOuNotFound', 'No encontramos esa organización. Elige otra de la lista.'],
  'ou-no-disponible': ['register.errOuUnavailable', 'El registro no está disponible para la organización seleccionada.'],
  'aceptacion-legal-requerida': ['register.errPrivacy', 'Debes aceptar el Aviso de Privacidad.'],
  'local-id-requerido': ['register.errLocalId', 'Introduce tu Local ID (hasta 8 dígitos).'],
  'nomina-requerida': ['register.errEmployeeId', 'Introduce tu número de nómina.'],
  'datos-no-validados': [
    'register.errNoMatch',
    'No pudimos validar tus datos con los registros de la empresa. Verifica que tu unidad organizacional y tu número de nómina (o tu Local ID, si eres de Europa) sean correctos. Si ya te registraste antes, inicia sesión o recupera tu contraseña. Si el problema continúa, contacta a Capital Humano.',
  ],
  'correo-en-uso': [
    'register.errEmailInUse',
    'Este correo electrónico ya tiene una cuenta asociada.',
  ],
  'nomina-ya-registrada': [
    'register.errPayrollTaken',
    'Este número de nómina ya tiene una cuenta asociada.',
  ],
  'demasiados-envios': [
    'register.errTooMany',
    'Demasiados intentos. Espera un momento y vuelve a intentarlo.',
  ],
  'correo-corporativo': [
    'register.corporateEmail',
    'Este correo es corporativo y ya cuentas con acceso mediante inicio de sesión Sigma (SSO), por lo que no puede usarse para registrarte. Entra con tu cuenta Sigma.',
  ],
};

/** El código de motivo del servidor, sólo si es uno de los del registro. */
export function motivoDeRegistro(error: unknown): string | undefined {
  const reason = getRecognitionErrorReason(error);
  return reason && reason in CLAVES_DE_REGISTRO ? reason : undefined;
}

/**
 * El texto para la persona cuando FALLA el envío del registro.
 *
 * Primero el código de motivo (traducido). Si no hay, se mira el TIPO de fallo: sin conexión, demasiados
 * intentos o un error del servidor —este último con el identificador de referencia, que es lo que le
 * sirve a soporte para encontrar el caso—. Sólo si nada de eso aplica se muestra el texto del servidor.
 */
export function mensajeDeErrorDeRegistro(error: unknown, t: TFunction): string {
  const motivo = motivoDeRegistro(error);
  if (motivo) {
    const [clave, porOmision] = CLAVES_DE_REGISTRO[motivo];
    return t(clave, porOmision);
  }

  const status = getRecognitionErrorStatus(error);
  if (status === 429) {
    return t('register.errTooMany', 'Demasiados intentos. Espera un momento y vuelve a intentarlo.');
  }
  if (status !== undefined && status >= 500) {
    const referencia = getRecognitionErrorCorrelationId(error) ?? '—';
    return t(
      'register.errServer',
      'Tuvimos un problema de nuestro lado y no pudimos completar el registro. Vuelve a intentarlo en unos minutos. Si continúa, avisa a soporte con esta referencia: {{referencia}}.',
      { referencia },
    );
  }
  if (esErrorTemporal(error)) {
    return t('register.errNetwork', 'No pudimos conectarnos. Revisa tu conexión y vuelve a intentarlo.');
  }
  return getRecognitionErrorMessage(error, t('register.submitError', 'No se pudo completar el registro.'));
}

/** Qué le pasó al enlace de verificación de correo. */
export type MotivoDeVerificacion = 'ya-verificado' | 'vencido' | 'invalido' | 'correo-en-uso' | 'temporal';

/**
 * Traduce el error de la verificación al caso que la pantalla tiene que explicar. Un fallo que no es del
 * enlace (sin conexión, servidor caído) es `temporal`: la persona debe reintentar, no pedir otro enlace.
 */
export function motivoDeVerificacion(error: unknown): MotivoDeVerificacion {
  switch (getRecognitionErrorReason(error)) {
    case 'correo-ya-verificado': return 'ya-verificado';
    case 'enlace-vencido': return 'vencido';
    case 'enlace-invalido': return 'invalido';
    // El enlace era bueno, pero otra persona verificó ese mismo correo antes (el correo se «amarra» a la cuenta
    // al verificarlo, y gana quien llega primero).
    case 'correo-en-uso': return 'correo-en-uso';
    default: return esErrorTemporal(error) ? 'temporal' : 'invalido';
  }
}

/**
 * El texto para la persona cuando falla el LOGIN. El servidor sólo manda un código cuando quien llama ya
 * acertó la contraseña (correo sin verificar o cuenta desactivada); en cualquier otro caso es el 401 con
 * un mensaje único.
 *
 * 🔴 Ese 401 NO se muestra con el texto del servidor: viene SÓLO en español, y con el idioma en inglés el modal
 * mostraba el título traducido y el mensaje en español (pruebas en DEV, 2026-10-08). La credencial incorrecta se
 * dice siempre con `login.errCredentials`, que está en los 7 idiomas. Un fallo que no es 401 ni trae código
 * conserva el texto del servidor como último recurso.
 */
export function mensajeDeErrorDeLogin(error: unknown, t: TFunction): string {
  switch (getRecognitionErrorReason(error)) {
    case 'correo-no-verificado':
      return t(
        'login.errNotVerified',
        'Tu correo aún no está verificado. Abre el enlace que te enviamos o pide otro con «¿No recibiste el correo de verificación?».',
      );
    case 'cuenta-desactivada':
      return t('login.errInactive', 'Tu cuenta está desactivada. Contacta a Capital Humano.');
    case 'contrasena-temporal-vencida':
      return t('login.errTempExpired', 'Tu contraseña temporal venció. Pide otra a Capital Humano.');
    default:
      if (esErrorTemporal(error)) {
        return t('login.errNetwork', 'No pudimos conectarnos. Revisa tu conexión y vuelve a intentarlo.');
      }
      if (getRecognitionErrorStatus(error) === 401) {
        return t('login.errCredentials', 'Correo o contraseña incorrectos.');
      }
      return getRecognitionErrorMessage(error, t('login.errCredentials', 'Correo o contraseña incorrectos.'));
  }
}
